import {
    Control,
    FieldErrors,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
} from "react-hook-form/dist/types";
import { CreateProgramPopup } from "../../../types/ClientProgram";
import { DropDownGeneric } from "../../../components/dropdowns";
import { API, API_BASEURL, buildUrl, DB_ENUMS, FORM_MSG, PERMISSIONS, SWR_CONFIG } from "../../../config";
import { ErrorText } from "../../../components/forms/ErrorText";
import { NumberFormatRFH } from "../../../components/inputs/NumberFormatRFH";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { DateInput } from "../../../components/inputs/DateInput";
import { MultiCheckbox } from "../../../components/inputs/MultiCheckbox";
import { CheckboxRFH } from "../../../components/inputs/CheckboxRFH";
import { AddPaymentMethod } from "../Payment/AddPaymentMethod";
import { useRouteMatch } from "react-router-dom";
import useGetSWR from "../../../hooks/useGetSWR";
import { PaymentMethodOption, PaymentMethodOptionRes, PaymentMethodType } from "../../../types";
import { usePermissions } from "../../../hooks";
import { EXTERNAL_PAYMENT_ELIGIBLE_PROGRAMS } from "../../../containers/contact-shared/programs/helpers/fields";

export const PaymentsDynamicFields = ({
    setValue,
    getErrorType,
    watch,
    control,
    register,
    errors,
    clientId,
    editing,
    person_id,
    isLead,
}: {
    setValue: UseFormSetValue<CreateProgramPopup>;
    getErrorType: (name: string) => string | undefined;
    watch: UseFormWatch<CreateProgramPopup>;
    control: Control<CreateProgramPopup>;
    register: UseFormRegister<CreateProgramPopup>;
    errors: FieldErrors<CreateProgramPopup>;
    clientId?: string;
    editing: boolean;
    person_id?: string;
    isLead?: boolean;
}) => {
    const { checkPermission } = usePermissions();

    let { url } = useRouteMatch();
    const pathParts = url.split("/");
    const id = pathParts[2];
    const paymentTypeOptions = ["XP", "Manual", "Waived"];
    const showPaymentValues = ["XP", "Manual"];

    const preferredPaymentMethodOptions = ["Cash", "Zelle", "Credit Card", "Bank Account"];
    const preferredToShowPaymentMethods = ["Credit Card", "Bank Account"];

    const hasCreatePermissions = useMemo(() => {
        if (isLead) return checkPermission(PERMISSIONS.LEAD_UPDATE_PAYMENT_METHODS);
        return checkPermission(PERMISSIONS.CLIENT_UPDATE_PAYMENT_METHODS);
    }, [checkPermission, isLead]);

    const paymentMethodOptionsUrl = buildUrl(isLead ? API.LEAD_PAYMENT_METHOD_OPTIONS : API.CLIENT_PAYMENT_METHOD_OPTIONS, { id: person_id || id || clientId }, API_BASEURL);

    const { data: paymentMethodOptionsRes, mutate: mutatePaymentMethodOptions } =
        useGetSWR<PaymentMethodOptionRes>(paymentMethodOptionsUrl, SWR_CONFIG.FORMS);

    const [paymentOptions, setPaymentOptions] = useState<PaymentMethodOption[]>([]);

    const updatePaymentOptions = (response: PaymentMethodOptionRes) => {
        const options = response.options;
        setPaymentOptions(options);
    };


    useEffect(() => {
        const isQuote = watch("quote");
        const isExternal =
            EXTERNAL_PAYMENT_ELIGIBLE_PROGRAMS.includes(watch("name") as string) &&
            watch("external_payment");
        const paymentRequired = !isQuote && !isExternal && watch("payment_type") !== "Waived";
        register("payment_method_id", { required: false });
        register("payment_type", { required: paymentRequired });
        register("estimated_deposit", { required: paymentRequired });
        register("num_payments", { required: paymentRequired });
        register("next_payment", { required: paymentRequired });
        register("external_payment", {});
    }, [watch("payment_type"), watch("quote"), watch("external_payment"), watch("name")]);

    useEffect(() => {
        const isQuote = watch("quote");
        const isExternal =
            EXTERNAL_PAYMENT_ELIGIBLE_PROGRAMS.includes(watch("name") as string) &&
            watch("external_payment");
        register("payment_interval", {
            required:
                !isQuote &&
                !isExternal &&
                watch("payment_type") !== "Waived" &&
                watch("num_payments") !== 0,
        });
    }, [
        watch("payment_type"),
        watch("num_payments"),
        watch("quote"),
        watch("external_payment"),
        watch("name"),
    ]);


    useEffect(() => {
        if (!paymentMethodOptionsRes) return;

        updatePaymentOptions(paymentMethodOptionsRes);
    }, [paymentMethodOptionsRes]);

    const calculateNextPayment = (days: number) => {
        const isMonthlyFrequency = 30;
        const LATEST_DAY_VALID = 28;
        const applyDiscount = watch("num_payments") === 0 && watch("apply_discount");
        let expected;

        if (applyDiscount) {
            return moment().format("YYYY-MM-DD");
        }

        if (days === isMonthlyFrequency) {
            expected = moment().add(1, "month");

            if (expected.date() > LATEST_DAY_VALID) {
                //expected debe pasar al día 1 del siguiente mes
                expected = expected.add(1, "month").date(1);
            }

            return expected.format("YYYY-MM-DD");
        }

        expected = moment().add(days, "days");

        return expected.format("YYYY-MM-DD");
    };

    useEffect(() => {
        const option = Number(watch("num_payments")) || 0;
        const total = Number(watch("price")) || 0;
        const deposit = Number(watch("estimated_deposit")) || 0;
        const applyDiscount = watch("num_payments") === 0 && watch("apply_discount");
        const isFinalValidation = true;
        const isValidating = false;

        if (applyDiscount) {
            setValue("recurrent_amount", parseFloat((total * 0.9).toFixed(2)), {
                shouldValidate: true,
            });
            return;
        }

        if (total !== 0 && option !== 0) {
            setValue("recurrent_amount", parseFloat(((total - deposit) / option).toFixed(2)), {
                shouldValidate: true,
            });
        } else if (total !== 0 && option === 0) {
            if (isFinalValidation || isValidating) {
                setValue("recurrent_amount", parseFloat(total.toFixed(2)), {
                    shouldValidate: true,
                });
            } else {
                setValue("recurrent_amount", parseFloat((total * 0.9).toFixed(2)), {
                    shouldValidate: true,
                });
            }
        } else {
            setValue("recurrent_amount", 0, { shouldValidate: true });
        }
    }, [watch("num_payments"), watch("price"), watch("estimated_deposit")]);

    const paymentInterval = watch("payment_interval");
    const nextPayment = watch("next_payment");

    const preferredPaymentMethod = watch("preferred_payment_method");

    useEffect(() => {
        if (paymentInterval) {
            const nextDate = calculateNextPayment(Number(paymentInterval));
            setValue("next_payment" as any, nextDate, { shouldValidate: true });
        }
    }, [paymentInterval]);

    useEffect(() => {
        if (watch("num_payments") === 0) {
            const total = Number(watch("price")) || 0;
            const applyDiscount = watch("apply_discount");

            setValue("estimated_deposit", 0, { shouldValidate: false });
            setValue("next_payment", moment().format("YYYY-MM-DD"), { shouldValidate: false });
            setValue("recurrent_amount", total * (applyDiscount ? 0.9 : 1), { shouldValidate: false });
            setValue("payment_interval", undefined, { shouldValidate: false });
        }
    }, [watch("num_payments"), watch("apply_discount")]);

    useEffect(() => {
        const programName = watch("name"); // o el campo que traiga "Funding"
        const fundingType = watch("funding_type"); // si lo tienes en el form

        if (programName === "Funding") {
            const autoDeposit = fundingType !== "Equity" ? 2500 : 0;
            setValue("estimated_deposit", autoDeposit, { shouldValidate: true });
        }
    }, [watch("name"), watch("funding_type")]);

    const filterPastDates = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Establecer la hora a medianoche para comparar solo fechas

        return date >= today;
    };

    const filterPastDateAnd30Days = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Establecer la hora a medianoche para comparar solo fechas

        return date >= today && date.getDate() <= 28;
    };

    const isExternalPaymentEligible = EXTERNAL_PAYMENT_ELIGIBLE_PROGRAMS.includes(
        watch("name") as string
    );

    return (
        <div className="lg:col-span-3 sm:col-span-4 col-span-8">
            {isExternalPaymentEligible && (
                <div
                    className="flex flex-col mb-4"
                    title={
                        watch("external_payment")
                            ? "Payment is managed by an external vendor."
                            : "Payment is managed by La Reyna del Crédito."
                    }
                >
                    <CheckboxRFH
                        editing={editing}
                        className="flex-col w-full"
                        titleClassname="text-r-medium-small-2"
                        title="External payment"
                        name="external_payment"
                        inputRegister={register}
                    />
                    <span className="text-r-small text-color7 italic mt-1">
                        {watch("external_payment")
                            ? "Payment is managed by an external vendor."
                            : "Payment is managed by La Reyna del Crédito."}
                    </span>
                </div>
            )}
            {!(isExternalPaymentEligible && watch("external_payment")) && (
                <DropDownGeneric
                    title="Payment Type"
                    name="payment_type"
                    placeholder="Payment Type"
                    className="flex-col"
                    titleClassname="text-r-medium-small-2"
                    inputClassName="mb-4"
                    menuPosition="fixed"
                    editing={editing}
                    readonly={false}
                    selectOptions={paymentTypeOptions}
                    isObject={false}
                    showBlankOption={false}
                    onChangeFunction={(val) => {
                        setValue("payment_type" as any, val);

                        // reset si NO es de los que muestran valores
                        if (!showPaymentValues.includes(val)) {
                            setValue("estimated_deposit", undefined);
                            setValue("num_payments", undefined);
                            setValue("recurrent_amount", undefined);
                            setValue("payment_interval", undefined);
                            setValue("next_payment", undefined);
                            setValue("payment_method_id", undefined);
                            setValue("preferred_payment_method", undefined);
                            setValue("dont_charge_deposit_auto", undefined);
                        }
                    }}
                    value={watch("payment_type") || ""}
                >
                    {getErrorType("payment_type") === "required" && (
                        <ErrorText msg={FORM_MSG.REQUIRED} />
                    )}
                </DropDownGeneric>
            )}

            {!watch("external_payment") && showPaymentValues.includes(watch("payment_type")!) && (
                <div className="w-full grid grid-cols-9 gap-2 text-r-medium-small-2 items-start">
                    <div
                        key="Num Payments"
                        className="col-span-3 w-full flex sm:flex-col items-center sm:items-start"
                    >
                        <DropDownGeneric
                            title="Num Payments"
                            inputClassName="w-full"
                            editing={editing}
                            readonly={false}
                            className="w-full"
                            value={watch("num_payments") === null || watch("num_payments") === undefined ? "" : watch("num_payments")}
                            name="num_payments"
                            onChangeFunction={(val) =>
                                setValue("num_payments" as any, val, { shouldValidate: true })
                            }
                            selectOptions={[...DB_ENUMS.PAYMENT_OPTIONS]}
                            isObject
                            objectKeyValue="value"
                            objectKeyDisplay="label"
                            menuPosition="fixed"
                        />
                        {getErrorType("num_payments") === "required" && (
                            <ErrorText msg={FORM_MSG.REQUIRED} />
                        )}
                        {getErrorType("num_payments") === "validate" && <ErrorText msg={""} />}
                    </div>
                    {watch("num_payments") !== 0 ? (
                        <>
                            <NumberFormatRFH
                                key="Estimated Deposit"
                                editing={watch("name") === "Funding" ? false : editing}
                                title="Estimated Deposit"
                                name="estimated_deposit"
                                inputClassName="!mb-0"
                                className="flex-col col-span-3"
                                control={control}
                                prefix="$"
                                suffix=""
                                inputRegister={{ ...register }}
                                validation={{
                                    required: true,
                                    validate: (value: number) => {
                                        if (watch("name") === "Tradelines Funding") {
                                            return (
                                                value >= 500 || "The value must be greater or equal to 500."
                                            );
                                        }
                                        return true; // si no es ese programa, siempre válido
                                    },
                                }}
                            >
                                <>
                                    {getErrorType("estimated_deposit") === "required" && (
                                        <ErrorText msg={FORM_MSG.REQUIRED} />
                                    )}
                                    {getErrorType("estimated_deposit") === "validate" && (
                                        <ErrorText msg={(errors as any)["estimated_deposit"].message} />
                                    )}
                                </>
                            </NumberFormatRFH>

                            <NumberFormatRFH
                                key="Recurrent Amount"
                                editing={false}
                                title="Recurrent Amount"
                                name="recurrent_amount"
                                inputClassName="!mb-0"
                                className="flex-col col-span-3"
                                control={control}
                                prefix="$"
                                suffix=""
                                inputRegister={{ ...register }}
                                validation={{ required: true }}
                            >
                                <>
                                    {getErrorType("recurrent_amount") === "required" && (
                                        <ErrorText msg={FORM_MSG.REQUIRED} />
                                    )}
                                    {getErrorType("recurrent_amount") === "validate" && (
                                        <ErrorText msg={(errors as any)["recurrent_amount"].message} />
                                    )}
                                </>
                            </NumberFormatRFH>

                            <div
                                key="Payment Interval"
                                className="col-span-3 w-full flex sm:flex-col items-center sm:items-start"
                            >
                                <DropDownGeneric
                                    title="Payment Interval"
                                    inputClassName="w-full"
                                    editing={editing}
                                    readonly={false}
                                    className="w-full"
                                    value={watch("payment_interval") || ""}
                                    name="payment_interval"
                                    onChangeFunction={(val) =>
                                        setValue("payment_interval" as any, val, { shouldValidate: true })
                                    }
                                    selectOptions={[...DB_ENUMS.PAYMENT_INTERVALS]}
                                    isObject
                                    objectKeyValue="value"
                                    objectKeyDisplay="label"
                                    menuPosition="fixed"
                                />
                                {getErrorType("payment_interval") === "required" && (
                                    <ErrorText msg={FORM_MSG.REQUIRED} />
                                )}
                                {getErrorType("payment_interval") === "validate" && <ErrorText msg={""} />}
                            </div>

                            <div
                                key="Next Payment"
                                className="col-span-3 w-full flex sm:flex-col items-center sm:items-start"
                            >
                                <DateInput
                                    title="Next Payment"
                                    val={nextPayment || ""}
                                    onChange={(val) => {
                                        setValue("next_payment" as any, val, { shouldValidate: true });
                                    }}
                                    editing={editing}
                                    className="w-full"
                                    inputClassName="!mb-0"
                                    dateFilter={
                                        watch("payment_interval") === 30
                                            ? filterPastDateAnd30Days
                                            : filterPastDates
                                    }
                                >
                                    <>
                                        {getErrorType("next_payment") === "required" && (
                                            <ErrorText msg={FORM_MSG.REQUIRED} />
                                        )}
                                    </>
                                </DateInput>
                            </div>
                        </>
                    ) : (
                        <div
                            className={`flex sm:flex-col items-center justify-center pt-6 col-span-3`}
                        >
                            <CheckboxRFH
                                editing={editing}
                                className="flex-col w-full"
                                titleClassname=""
                                title={"Apply 10% discount"}
                                name={"apply_discount"}
                                inputRegister={register}
                            />
                            {getErrorType("apply_discount") === "required" && (
                                <ErrorText msg={FORM_MSG.REQUIRED} />
                            )}
                        </div>
                    )}
                </div>
            )}

            {!watch("external_payment") && watch("payment_type") === "XP" && (
                <div className="col-span-2 w-full flex flex-col items-center sm:items-start">
                    <MultiCheckbox
                        title="Payment Options"
                        options={paymentOptions.map((opt) => ({
                            label: opt.value,
                            value: opt.id,
                        }))}
                        value={watch("payment_method_id") || ""}
                        onChangeFunction={(val) => {
                            const selectedId = val.split(",").filter(Boolean).at(-1) || "";
                            const selectedOption = paymentOptions.find((o) => o.id === selectedId);

                            setValue("payment_method_id" as any, selectedId, {
                                shouldValidate: true,
                            });
                            setValue(
                                "preferred_payment_method" as any,
                                selectedOption?.value || "",
                                { shouldValidate: true }
                            );
                        }}
                        optionsInColumns
                        editing={editing}
                    />

                    {getErrorType("payment_method_id") === "required" && (
                        <ErrorText msg={FORM_MSG.REQUIRED} />
                    )}
                    {editing && hasCreatePermissions && <AddPaymentMethodButton mutate={mutatePaymentMethodOptions} isLead={isLead} />}
                    {/** Uncomment when checkbox is needed */}
                    {/* <CheckboxRFH
                        title="Don't charge Deposit Auto"
                        name="dont_charge_deposit_auto"
                        inputRegister={register}
                    /> */}
                </div>
            )}

            {!watch("external_payment") && watch("payment_type") === "Manual" && (
                <div className="flex flex-col gap-2">
                    <DropDownGeneric
                        title="Preferred Payment Method"
                        name="preferred_payment_method"
                        className="flex-col"
                        titleClassname="text-r-medium-small-2 w-full"
                        menuPosition="fixed"
                        editing={editing}
                        readonly={false}
                        selectOptions={preferredPaymentMethodOptions}
                        isObject={false}
                        showBlankOption={false}
                        onChangeFunction={(val) => {
                            setValue("preferred_payment_method" as any, val);
                            setValue("payment_method_id" as any, undefined);
                        }}
                        value={watch("preferred_payment_method") || ""}
                    >
                        {getErrorType("preferred_payment_method") === "required" && (
                            <ErrorText msg={FORM_MSG.REQUIRED} />
                        )}
                    </DropDownGeneric>

                    {preferredToShowPaymentMethods.includes(watch("preferred_payment_method")!) && (
                        <div className="col-span-2 w-full flex flex-col items-center sm:items-start">
                            <MultiCheckbox
                                title="Payment Options"
                                editing={editing}
                                options={paymentOptions
                                    .filter((option) => {
                                        const preferred = watch("preferred_payment_method");
                                        if (preferred === "Credit Card")
                                            return option.value.includes("Credit Card");
                                        if (preferred === "Bank Account")
                                            return option.value.includes("Bank Account");
                                        return false;
                                    })
                                    .map((opt) => ({
                                        label: opt.value,
                                        value: opt.id,
                                    }))}
                                value={watch("payment_method_id") || ""}
                                onChangeFunction={(val) => {
                                    const selectedId = val.split(",").filter(Boolean).at(-1) || "";
                                    const selectedOption = paymentOptions.find(
                                        (o) => o.id === selectedId
                                    );

                                    setValue("payment_method_id" as any, selectedId, {
                                        shouldValidate: true,
                                    });
                                    setValue(
                                        "preferred_payment_method" as any,
                                        selectedOption?.value || "",
                                        { shouldValidate: true }
                                    );
                                }}
                                optionsInColumns
                            />

                            {editing && hasCreatePermissions && (
                                <AddPaymentMethodButton
                                    selectedPaymentMethod={preferredPaymentMethod}
                                    mutate={mutatePaymentMethodOptions}
                                    isLead={isLead}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface AddPaymentMethodButtonProps {
    selectedPaymentMethod?: string;
    mutate: Function;
    isLead?: boolean;
}

const AddPaymentMethodButton = ({ selectedPaymentMethod, mutate, isLead }: AddPaymentMethodButtonProps) => {
    const [showForm, setShowForm] = useState(false);

    return showForm ? (
        <AddPaymentMethod
            key={selectedPaymentMethod}
            selectedPaymentMethod={selectedPaymentMethod as PaymentMethodType}
            onCancel={() => setShowForm(false)}
            mutate={mutate}
            isLead={isLead}
        />
    ) : (
        <button
            type="button"
            className="self-center flex p-3 justify-center items-center gap-4 rounded-md bg-color2 text-r-medium-small-2"
            onClick={() => setShowForm(true)}
        >
            Add Payment Method
        </button>
    );
};
